import { ArrowLeft, Shield } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [biz, setBiz] = useState({});

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/business-info`).then(r => r.ok ? r.json() : {}).then(setBiz).catch(() => {});
  }, []);

  const contactLine = [biz.name, biz.address, [biz.city, biz.state, biz.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const contactInfo = contactLine
    ? `If you have questions about this Privacy Policy or our data practices, please contact us:\n\n${biz.name || 'DJsandMCMedia™'}\n${contactLine}\n${biz.email ? 'Email: ' + biz.email : ''}\n${biz.phone ? 'Phone: ' + biz.phone : ''}\n\nYou can also reach us through the Support Ticket system in your dashboard.`
    : `If you have questions about this Privacy Policy or our data practices, please contact us at support@djsandmcmedia.com or through the Support Ticket system in your dashboard.`;

  const sections = [
    {
      title: "1. Information We Collect",
      content: `We collect information you provide directly when you create an account, set up a channel, or contact support. This includes your name, email address, payment information, and channel configuration data. We also automatically collect usage data such as IP addresses, browser type, pages visited, and listening activity through our platform.`
    },
    {
      title: "2. How We Use Your Information",
      content: `We use your information to:\n\n(a) Provide, maintain, and improve our channel hosting services;\n(b) Process transactions and send related information including confirmations and invoices;\n(c) Send technical notices, updates, security alerts, and support messages;\n(d) Respond to your comments, questions, and customer service requests;\n(e) Monitor and analyze trends, usage, and activities in connection with our services;\n(f) Detect, investigate, and prevent fraudulent transactions and other illegal activities.`
    },
    {
      title: "3. Information Sharing",
      content: `We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:\n\n(a) With service providers who assist us in operating our platform (payment processors, email services, hosting providers);\n(b) To comply with legal obligations, court orders, or government requests;\n(c) To protect the rights, property, or safety of DJsandMCMedia™, our users, or the public;\n(d) With your consent or at your direction.`
    },
    {
      title: "4. Data Retention",
      content: `We retain your personal information for as long as your account is active or as needed to provide you services. If you request account deletion, we will remove your personal data within 30 days, except where we are required to retain certain information for legal or legitimate business purposes.`
    },
    {
      title: "5. Channel Operator Responsibilities",
      content: `Channel operators who collect listener data through their channels (e.g., chat messages, listener analytics, song requests) are responsible for their own compliance with applicable privacy laws, including but not limited to GDPR, CCPA, and other regional data protection regulations. DJsandMCMedia™ provides tools but is not responsible for how channel operators use listener data.`
    },
    {
      title: "6. Cookies & Tracking",
      content: `We use cookies and similar tracking technologies to maintain your session, remember your preferences, and analyze platform usage. You can control cookies through your browser settings. Disabling cookies may affect the functionality of certain features, such as staying logged in or remembering your player preferences.`
    },
    {
      title: "7. Third-Party Services",
      content: `Our platform integrates with third-party services including AzuraCast, PayPal, Google OAuth, and others. These services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services. When you use Google Sign-In, we receive your name, email, and profile picture from Google.`
    },
    {
      title: "8. Data Security",
      content: `We implement appropriate technical and organizational security measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. This includes encrypted data transmission (HTTPS/TLS), secure password hashing, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.`
    },
    {
      title: "9. Children's Privacy",
      content: `Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have collected personal data from a child without parental consent, we will take steps to delete that information promptly.`
    },
    {
      title: "10. Your Rights",
      content: `Depending on your location, you may have the following rights regarding your personal data:\n\n(a) Access — Request a copy of the personal data we hold about you;\n(b) Correction — Request correction of inaccurate or incomplete data;\n(c) Deletion — Request deletion of your personal data;\n(d) Portability — Request a copy of your data in a portable format;\n(e) Opt-out — Opt out of marketing communications at any time.\n\nTo exercise any of these rights, contact us through the Support Ticket system in your dashboard or email us at the address below.`
    },
    {
      title: "11. Changes to This Policy",
      content: `We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the revised policy.`
    },
    {
      title: "12. Contact Us",
      content: contactInfo
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <SEO title="Privacy Policy" description="Privacy policy for DJsandMCMedia™. Learn how we collect, use, and protect your data." noindex={true} />
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-20">
        <button onClick={() => navigate('/')} data-testid="privacy-back" className="inline-flex items-center gap-2 text-white/40 hover:text-white/70 transition text-sm mb-6">
          <ArrowLeft size={16} /> Back to Home
        </button>

        <div className="flex items-center gap-3 mb-2">
          <Shield className="text-purple-400" size={32} />
          <h1 data-testid="privacy-heading" className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
        </div>
        <p className="text-white/40 text-sm mb-10">Last updated: March 14, 2026</p>

        <div className="space-y-6">
          {sections.map((section, i) => (
            <div key={i} data-testid={`privacy-section-${i}`} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6">
              <h2 className="text-lg font-semibold text-white mb-3">{section.title}</h2>
              <p className="text-white/60 text-sm leading-relaxed whitespace-pre-line">{section.content}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-white/30 text-sm">DJsandMCMedia™ — The Shopify for Radio Creators</p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
